import { forwardRef, useState } from 'react';
import { B, font, weight } from '../../constants/brand';
import { Icon } from '../ui';

// One round, ringed button for every icon in the top header, so the bell, the
// feedback button and the rest cannot drift apart in size, ring or badge.
//
// THE COUNT IS NAVY, NEVER RED. A count says "something is waiting", not
// "something is wrong"; red on six icons at once teaches people to ignore red.
// An icon with a count also gets a darker ring, which is what makes it stand
// out. A count of 0 shows nothing at all.
//
// The label is a real tooltip and the aria-label, because an icon-only header
// is unusable without both.

export const formatCount = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  return v > 99 ? '99+' : String(Math.floor(v));
};

export const HeaderIconButton = forwardRef(function HeaderIconButton(
  { icon, label, count, onClick, active = false, ariaLabel, ...rest }, ref) {
  const [hover, setHover] = useState(false);
  const badge = formatCount(count);
  const lit = hover || active;
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        aria-label={ariaLabel || (badge ? `${label} (${badge})` : label)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        style={{
          position: 'relative', width: 36, height: 36, borderRadius: '50%', padding: 0,
          display: 'grid', placeItems: 'center', cursor: 'pointer', fontFamily: 'inherit',
          background: lit ? B.surfaceMuted : B.surface,
          border: `1.5px solid ${badge || lit ? B.navy : '#c9dfdc'}`,
          color: B.navy, transition: 'background 0.15s, border-color 0.15s',
        }}
        {...rest}
      >
        <Icon name={icon} size={17} />
        {badge ? (
          <span aria-hidden="true" style={{
            position: 'absolute', top: -3, right: -4, minWidth: 15, height: 15, padding: '0 4px',
            boxSizing: 'border-box', borderRadius: 8, background: B.navy, color: B.white,
            fontSize: 9.5, fontWeight: weight.semibold, lineHeight: '15px', textAlign: 'center',
            boxShadow: `0 0 0 2px ${B.appBg}`, fontVariantNumeric: 'tabular-nums',
          }}>{badge}</span>
        ) : null}
      </button>
      {hover && !active ? (
        <span role="tooltip" style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          whiteSpace: 'nowrap', background: B.text, color: B.white, fontSize: font.xs || 12,
          padding: '4px 8px', borderRadius: 6, pointerEvents: 'none', zIndex: 120,
        }}>{badge ? `${label} · ${badge}` : label}</span>
      ) : null}
    </span>
  );
});
